import React, { useEffect, useState } from "react";
import { Edit, Trash2 , Search  } from "lucide-react";
import { productService } from "../../services/productService";
import DeleteConfirmation from "../common/DeleteConfirmation";
import ProductForm from "./ProductForm";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LazyLoad from "react-lazyload";
import debounce from "lodash/debounce";

const ProductList = ({ categories, viewMode, setProducts }) => {
  const [products, setLocalProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = debounce((value) => {
    setCurrentPage(1);
    fetchProducts(1, false, value);
  }, 500);

  useEffect(() => {
    fetchProducts();
    return () => debouncedSearch.cancel();
  }, []);

  const fetchProducts = async (
    page = 1,
    loadMore = false,
    search = searchTerm
  ) => {
    try {
      setLoading(!loadMore);
      setLoadingMore(loadMore);

      const response = await productService.getProducts(page, 10, search);

      if (loadMore) {
        setLocalProducts((prev) => [...prev, ...response.products]);
      } else {
        setLocalProducts(response.products);
      }

      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products!");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const loadMoreProducts = () => {
    if (currentPage < totalPages) {
      fetchProducts(currentPage + 1, true);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      setLoading(true);
      await productService.deleteProduct(selectedProduct._id);
      setLocalProducts(
        products.filter((prod) => prod._id !== selectedProduct._id)
      );
      setProducts(products.filter((prod) => prod._id !== selectedProduct._id));
      setIsDeleteModalOpen(false);
      toast.success("Product deleted successfully!");
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error("Failed to delete product!");
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = async (formData) => {
    try {
      setLoading(true);
      const updatedProduct = await productService.updateProduct(
        selectedProduct._id,
        formData
      );
      const updatedProducts = products.map((prod) =>
        prod._id === updatedProduct._id ? updatedProduct : prod
      );
      setLocalProducts(updatedProducts);
      setProducts(updatedProducts);
      setIsEditModalOpen(false);
      toast.success("Product updated successfully!");
    } catch (error) {
      console.error("Failed to update product:", error);
      toast.error("Failed to update product!");
    } finally {
      setLoading(false);
    }
  };

  const renderGridView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="spinner border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {products.map((product) => (
          <div
            key={product._id}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
          >
            <LazyLoad height={200} offset={100} once>
              <img
                src={product.imageCover}
                alt={product.title}
                className="w-full h-48 object-cover"
                loading="lazy"
              />
            </LazyLoad>
            <div className="p-4">
              <h3 className="text-xl font-bold mb-2">{product.title}</h3>
              <p className="text-sm text-gray-600 mb-2">
                {product.category?.name}
              </p>
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-green-600">
                  ${product.price}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedProduct(product);
                      setIsEditModalOpen(true);
                    }}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedProduct(product);
                      setIsDeleteModalOpen(true);
                    }}
                    className="text-red-500 hover:text-red-700"
                    disabled={loading}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    if (loading && !loadingMore) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="spinner border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
        </div>
      );
    }

    return (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {products.map((product) => (
          <div
            key={product._id}
            className="flex items-center justify-between p-4 border-b hover:bg-gray-50 transition"
          >
            <div className="flex items-center space-x-4">
              <LazyLoad height={200} offset={100} once>
                <img
                  src={product.imageCover}
                  alt={product.title}
                  className="w-16 h-16 rounded-full object-cover"
                  loading="lazy"
                />
              </LazyLoad>
              <div>
                <h3 className="text-lg font-semibold">{product.title}</h3>
                <p className="text-sm text-gray-500">
                  {product.category?.name}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-green-600 font-bold">${product.price}</span>
              <button
                onClick={() => {
                  setSelectedProduct(product);
                  setIsEditModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700"
              >
                <Edit size={20} />
              </button>
              <button
                onClick={() => {
                  setSelectedProduct(product);
                  setIsDeleteModalOpen(true);
                }}
                className="text-red-500 hover:text-red-700"
                disabled={loading}
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
          <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full px-4 py-2 pl-10 pr-4 border rounded-lg focus:outline-none focus:border-blue-500"
          />
          <Search  className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
      </div>
      {viewMode === "grid" ? renderGridView() : renderListView()}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteProduct}
        itemName="product"
      />

      {selectedProduct && (
        <ProductForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditProduct}
          initialData={selectedProduct}
          categories={categories}
        />
      )}

      {currentPage < totalPages && (
        <div className="flex justify-center mt-6">
          <button
            onClick={loadMoreProducts}
            disabled={loadingMore}
            className={`px-4 py-2 rounded-lg ${
              loadingMore
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </>
  );
};

export default ProductList;
